import { queryClient } from '@/lib/query-client';
import { createQueryKeys } from '@/lib/query-factory';
import {
  EditableWorkorderMaterialFields,
  NewWorkorderMaterial,
  Workorder,
  WorkorderLabor,
  WorkorderMaterial,
} from '@certhon/models';
import { MutationOptions, queryOptions } from '@tanstack/react-query';
import { toast } from 'sonner';
import * as api from './workorders.api';
import type {
  WorkorderFilter,
  EditableWorkorderFieldsDto,
  EditableWorkorderLaborFieldsDto,
  NewWorkorderLaborDto,
} from './workorders.api';
import { myHoursQueries } from '@/features/my-hours/my-hours.queries';
import { dayPlanningQueries } from './dayplanning/dayplanning.queries';

function invalidateRelatedToWorkorder(workorderRecnum: number) {
  queryClient.invalidateQueries({ queryKey: keys.lists() });
  queryClient.invalidateQueries({ queryKey: keys.detail(workorderRecnum) });
  queryClient.invalidateQueries({ queryKey: dayPlanningQueries.all() });
  queryClient.invalidateQueries({ queryKey: myHoursQueries.keys.all });
}

const keys = createQueryKeys('workorders');
const laborKeys = createQueryKeys('workorder-labor');
const materialKeys = createQueryKeys('workorder-materials');

// ============================================================================
// Workorder Queries
// ============================================================================

export const workorderQueries = {
  keys,

  list: (filters?: WorkorderFilter) =>
    queryOptions({
      queryKey: keys.list(filters),
      queryFn: () => api.fetchWorkorders(filters),
    }),

  detail: (recnum: number) =>
    queryOptions({
      queryKey: keys.detail(recnum),
      queryFn: () => api.fetchWorkorder(recnum),
      enabled: !!recnum,
    }),

  labor: (workorderRecnum: number) =>
    queryOptions({
      queryKey: [...laborKeys.list(), workorderRecnum],
      queryFn: () => api.fetchWorkorderLabor(workorderRecnum),
      enabled: !!workorderRecnum,
    }),

  laborDetail: (recnum: number) =>
    queryOptions({
      queryKey: laborKeys.detail(recnum),
      queryFn: () => api.fetchLaborEntry(recnum),
      enabled: !!recnum,
    }),

  materials: (workorderRecnum: number) =>
    queryOptions({
      queryKey: [...materialKeys.list(), workorderRecnum],
      queryFn: () => api.fetchWorkorderMaterials(workorderRecnum),
      enabled: !!workorderRecnum,
    }),

  materialDetail: (recnum: number) =>
    queryOptions({
      queryKey: materialKeys.detail(recnum),
      queryFn: () => api.fetchMaterialEntry(recnum),
      enabled: !!recnum,
    }),
};

// ============================================================================
// Workorder Mutations
// ============================================================================

export const workorderMutations = {
  update: (): MutationOptions<
    Workorder,
    Error,
    { recnum: number; data: EditableWorkorderFieldsDto }
  > => ({
    mutationFn: ({ recnum, data }) => api.updateWorkorder(recnum, data),
    onSuccess: (workorder) => {
      invalidateRelatedToWorkorder(workorder.recnum);
      toast.success('Workorder updated');
    },
    onError: (error: Error) => {
      toast.error('Failed to update workorder', {
        description: error.message,
      });
    },
  }),

  /**
   * Complete a workorder without signature.
   * Triggers backend logic: copy prematerials, build pdf_task snapshot, etc.
   */
  complete: (
    recnum: number,
  ): MutationOptions<void, Error, { email?: string }> => ({
    mutationFn: ({ email }) => api.completeWorkorder(recnum, email),
    onSuccess: () => {
      invalidateRelatedToWorkorder(recnum);
      toast.success('Workorder completed');
    },
    onError: (error: Error) => {
      toast.error('Failed to complete workorder', {
        description: error.message,
      });
    },
  }),

  /**
   * Sign and complete a workorder with customer signature.
   * Triggers backend logic and sends email notification to customer.
   */
  sign: (
    recnum: number,
  ): MutationOptions<
    void,
    Error,
    { signature_data: string; email: string }
  > => ({
    mutationFn: (data) => api.signWorkorder(recnum, data),
    onSuccess: () => {
      invalidateRelatedToWorkorder(recnum);
      toast.success('Workorder signed and completed');
    },
    onError: (error: Error) => {
      toast.error('Failed to sign workorder', {
        description: error.message,
      });
    },
  }),
};

// ============================================================================
// Labor Mutations
// ============================================================================

export const laborMutations = {
  create: (
    workorderRecnum: number,
  ): MutationOptions<WorkorderLabor, Error, NewWorkorderLaborDto> => ({
    mutationFn: (data) => api.createLaborEntry(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...laborKeys.list(), workorderRecnum],
      });
      invalidateRelatedToWorkorder(workorderRecnum);
      toast.success('Labor entry added');
    },
    onError: (error: Error) => {
      toast.error('Failed to add labor entry', {
        description: error.message,
      });
    },
  }),

  update: (
    workorderRecnum: number,
  ): MutationOptions<
    WorkorderLabor,
    Error,
    { recnum: number; data: EditableWorkorderLaborFieldsDto }
  > => ({
    mutationFn: ({ recnum, data }) => api.updateLaborEntry(recnum, data),
    onSuccess: (labor) => {
      queryClient.invalidateQueries({
        queryKey: [...laborKeys.list(), workorderRecnum],
      });
      queryClient.invalidateQueries({
        queryKey: laborKeys.detail(labor.recnum),
      });
      invalidateRelatedToWorkorder(workorderRecnum);
      toast.success('Labor entry updated');
    },
    onError: (error: Error) => {
      toast.error('Failed to update labor entry', {
        description: error.message,
      });
    },
  }),

  delete: (workorderRecnum: number): MutationOptions<void, Error, number> => ({
    mutationFn: (recnum) => api.deleteLaborEntry(recnum),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...laborKeys.list(), workorderRecnum],
      });
      invalidateRelatedToWorkorder(workorderRecnum);
      toast.success('Labor entry deleted');
    },
    onError: (error: Error) => {
      toast.error('Failed to delete labor entry', {
        description: error.message,
      });
    },
  }),
};

// ============================================================================
// Material Mutations
// ============================================================================

export const materialMutations = {
  create: (
    workorderRecnum: number,
  ): MutationOptions<WorkorderMaterial, Error, NewWorkorderMaterial> => ({
    mutationFn: (data) => api.createMaterialEntry(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...materialKeys.list(), workorderRecnum],
      });
      toast.success('Material added');
    },
    onError: (error: Error) => {
      toast.error('Failed to add material', {
        description: error.message,
      });
    },
  }),

  update: (
    workorderRecnum: number,
  ): MutationOptions<
    WorkorderMaterial,
    Error,
    { recnum: number; data: EditableWorkorderMaterialFields }
  > => ({
    mutationFn: ({ recnum, data }) => api.updateMaterialEntry(recnum, data),
    onSuccess: (material) => {
      queryClient.invalidateQueries({
        queryKey: [...materialKeys.list(), workorderRecnum],
      });
      queryClient.invalidateQueries({
        queryKey: materialKeys.detail(material.recnum),
      });
      toast.success('Material updated');
    },
    onError: (error: Error) => {
      toast.error('Failed to update material', {
        description: error.message,
      });
    },
  }),

  delete: (workorderRecnum: number): MutationOptions<void, Error, number> => ({
    mutationFn: (recnum) => api.deleteMaterialEntry(recnum),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...materialKeys.list(), workorderRecnum],
      });
      toast.success('Material deleted');
    },
    onError: (error: Error) => {
      toast.error('Failed to delete material', {
        description: error.message,
      });
    },
  }),
};
